/**
 * You — the profile and the goals.
 *
 * What belongs here is everything about the *user* rather than about a day.
 * That includes the **goals**, which used to be a route with no tab: you could
 * set a daily budget from a link on Today and then have nowhere to go to find
 * it again, because the navigation never admitted the page existed. A budget
 * and a target weight are facts about the user, the same kind of thing as the
 * timezone below them.
 *
 * Chiefly the timezone — the backend buckets every log into a local date at
 * write time and never recomputes it, so a profile created with the wrong zone
 * quietly files evening meals on the following day, forever, with no way to
 * say so from any screen. That is the reason this page is a settings form
 * first and nothing else second.
 *
 * The saved foods and the composed meals used to be here too, and are not any
 * more: they moved to **Foods**, along with the by-hand form and the product
 * correction, which were the same act done from three unrelated screens. What
 * is left is the user, which is what the tab is called.
 *
 * Weight is deliberately not editable here. It is a series, not a field
 * (`models/user.py`), and the Weight page owns it; a second box to type it in
 * would be a second source of truth for the one number the whole trend rests on.
 */

import {
  deviceTimeZone,
  localDate,
  parseDecimal,
  useProfile,
  useUpdateProfile,
  type Profile,
  type ProfileUpdate,
} from "@calorio/core";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";

import { useCalorio } from "../app/providers";
import { useSession } from "../app/session";
import {
  describePasskeyError,
  listPasskeys,
  passkeysSupported,
  registerPasskey,
  removePasskey,
  type PasskeySummary,
} from "../lib/passkeys";
import { GoalsSection } from "./Goals";

const SEXES: { value: Profile["sex"]; label: string }[] = [
  { value: "unspecified", label: "Rather not say" },
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
];

/** The labels say what the level *is* in a week, because "moderate" means
 *  nothing until you know whose moderate. */
const ACTIVITY: { value: Profile["activity_level"]; label: string }[] = [
  { value: "sedentary", label: "Sedentary — desk work, little exercise" },
  { value: "light", label: "Light — 1–2 sessions a week" },
  { value: "moderate", label: "Moderate — 3–4 sessions a week" },
  { value: "active", label: "Active — 5–6 sessions a week" },
  { value: "very_active", label: "Very active — daily, or physical work" },
];

const GOALS: { value: Profile["goal"]; label: string }[] = [
  { value: "lose", label: "Lose weight" },
  { value: "maintain", label: "Maintain" },
  { value: "gain", label: "Gain weight" },
];

/**
 * Every zone the platform knows, with the profile's own guaranteed present.
 *
 * `Intl.supportedValuesOf` is missing on Safari before 15.4, and a select with
 * no options would strand exactly the user who most needs to fix their zone.
 * The fallback is the two that matter: what's stored, and where the device
 * thinks it is.
 */
function timeZones(current: string): string[] {
  const device = deviceTimeZone();
  let all: string[] = [];
  try {
    all = Intl.supportedValuesOf?.("timeZone") ?? [];
  } catch {
    all = [];
  }
  return [...new Set([...all, current, device])].sort();
}

export function You() {
  const ctx = useCalorio();
  const profile = useProfile(ctx);
  const { hash } = useLocation();

  /**
   * Arriving at `/you#goals` should land on the goals, not the top of the page.
   *
   * The router does not do this for us — it owns the history entry, not the
   * scroll position — and the two links that aim here ("Set a daily budget",
   * "Adjust goal") are answering a question the user asked somewhere else. The
   * effect waits for the section to exist, because the goals load their own
   * data and are not in the DOM on the first frame.
   */
  useEffect(() => {
    if (hash !== "#goals") return;
    const scroll = () => document.getElementById("goals")?.scrollIntoView({ block: "start" });
    scroll();
    const retry = setTimeout(scroll, 200);
    return () => clearTimeout(retry);
  }, [hash, profile.data]);

  if (profile.isLoading) return <p className="muted">Loading…</p>;
  if (!profile.data) return <p className="error">Couldn't load your profile.</p>;

  return (
    <div className="stack">
      <header>
        <h1 style={{ fontSize: "var(--step-2)", margin: 0 }}>You</h1>
        <p className="stat__label" style={{ margin: 0 }}>
          Your goals, and the timezone your days are bucketed by. Foods and
          composed meals live on{" "}
          <Link to="/foods" className="muted">
            Foods
          </Link>
          .
        </p>
      </header>

      {/* First, because it is the only thing on this page that changes what
          every other screen shows. */}
      <GoalsSection />

      {/* Keyed on the loaded profile so the form's initial state comes from
          real data rather than from a first render that had none. */}
      <ProfileCard key={profile.data.identity_id} profile={profile.data} />

      <SignInCard />
    </div>
  );
}

/**
 * How you get in, and out: this device's passkeys for Calorio, and signing out.
 *
 * A passkey is added from an existing session (Authgate refuses otherwise),
 * which is why this lives here rather than on the sign-in screen; after that,
 * "Sign in with a passkey" there needs no email. Authgate emails the account
 * whenever one is added. Removing one is always safe: the emailed code stays.
 *
 * Signing out is here as well as on Weight, where it is only shown on a phone,
 * so there is one place to find it at every width.
 */
function SignInCard() {
  const { signOut } = useSession();
  const supported = passkeysSupported();
  const [keys, setKeys] = useState<PasskeySummary[] | null>(null);
  const [label, setLabel] = useState(defaultPasskeyLabel);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = async () => {
    try {
      setKeys(await listPasskeys());
    } catch {
      setKeys([]);
      setProblem("Couldn't load your passkeys.");
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const add = async () => {
    setProblem(null);
    setNotice(null);
    setBusy(true);
    try {
      await registerPasskey(label);
      setNotice("Passkey added. Next time, choose \"Sign in with a passkey\".");
      await reload();
    } catch (error) {
      setProblem(describePasskeyError(error));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (key: PasskeySummary) => {
    if (!window.confirm(`Remove the passkey "${key.label}"? You can still sign in with an emailed code.`)) return;
    setProblem(null);
    setNotice(null);
    setBusy(true);
    try {
      await removePasskey(key.credentialId);
      await reload();
    } catch (error) {
      setProblem((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack" style={{ gap: "var(--sp-3)" }} aria-labelledby="signin-title">
      <div>
        <h2 id="signin-title" className="card__title" style={{ marginBottom: 0 }}>
          Sign-in
        </h2>
        <p className="stat__label" style={{ margin: "var(--sp-1) 0 0" }}>
          A passkey signs you in to Calorio with this device's fingerprint, face
          or screen lock instead of an emailed code.
        </p>
      </div>

      {keys === null ? (
        <p className="muted" style={{ margin: 0 }}>Loading…</p>
      ) : keys.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>No passkeys yet.</p>
      ) : (
        <ul className="stack" style={{ gap: "var(--sp-2)", listStyle: "none", margin: 0, padding: 0 }}>
          {keys.map((key) => (
            <li
              key={key.credentialId}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--sp-2)" }}
            >
              <span>
                {key.label}
                <span className="stat__label" style={{ display: "block" }}>
                  added {formatWhen(key.createdAt)}
                  {key.lastUsedAt ? `, last used ${formatWhen(key.lastUsedAt)}` : ", not used yet"}
                </span>
              </span>
              <button
                type="button"
                className="signin__secondary"
                onClick={() => void remove(key)}
                disabled={busy}
                aria-label={`Remove passkey ${key.label}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {supported ? (
        <>
          <label className="stack" style={{ gap: "var(--sp-1)" }}>
            <span>Name for this device's passkey</span>
            <input
              className="field"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={60}
              aria-label="Passkey name"
            />
          </label>
          <button className="button" type="button" onClick={() => void add()} disabled={busy}>
            {busy ? "…" : "Add a passkey"}
          </button>
        </>
      ) : (
        <p className="muted" style={{ margin: 0 }}>This browser can't use passkeys.</p>
      )}

      {problem && <p className="error" style={{ margin: 0 }}>{problem}</p>}
      {notice && <p className="muted" style={{ margin: 0 }}>{notice}</p>}

      <button type="button" className="signin__secondary" onClick={() => void signOut()}>
        Sign out
      </button>
    </section>
  );
}

/** "iPhone", "Mac", … — a starting point for the name, which the person can change. */
function defaultPasskeyLabel(): string {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  return "Passkey";
}

function formatWhen(iso: string): string {
  const when = new Date(iso);
  return Number.isNaN(when.getTime())
    ? iso
    : when.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function ProfileCard({ profile }: { profile: Profile }) {
  const ctx = useCalorio();
  const save = useUpdateProfile(ctx);

  const [timezone, setTimezone] = useState(profile.timezone);
  const [sex, setSex] = useState<Profile["sex"]>(profile.sex);
  const [birthDate, setBirthDate] = useState(profile.birth_date ?? "");
  const [height, setHeight] = useState(profile.height_cm ? String(profile.height_cm) : "");
  const [activity, setActivity] = useState<Profile["activity_level"]>(profile.activity_level);
  const [goal, setGoal] = useState<Profile["goal"]>(profile.goal);
  const [problem, setProblem] = useState<string | null>(null);

  const zones = useMemo(() => timeZones(profile.timezone), [profile.timezone]);

  // Typing drops the stale "Saved" note, which would otherwise sit there
  // asserting that whatever is now in the fields is what was stored.
  const edit =
    <T,>(set: (v: T) => void) =>
    (value: T) => {
      set(value);
      setProblem(null);
      save.reset();
    };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);

    const changes: ProfileUpdate = {};
    if (timezone !== profile.timezone) changes.timezone = timezone;
    if (sex !== profile.sex) changes.sex = sex;
    if (activity !== profile.activity_level) changes.activity_level = activity;
    if (goal !== profile.goal) changes.goal = goal;

    const storedBirth = profile.birth_date ?? "";
    if (birthDate !== storedBirth) changes.birth_date = birthDate || null;

    const storedHeight = profile.height_cm ? String(profile.height_cm) : "";
    if (height !== storedHeight) {
      if (!height.trim()) {
        changes.height_cm = null;
      } else {
        const cm = parseDecimal(height);
        // The same bounds the backend's validator enforces, checked here so a
        // typo is a sentence under the field rather than a 422 the form has to
        // translate.
        if (!Number.isFinite(cm) || cm <= 0 || cm >= 300)
          return setProblem("Height must be a number in centimetres, under 300.");
        changes.height_cm = cm;
      }
    }

    // Nothing to send is not an error, but it must not look like a save either.
    if (!Object.keys(changes).length) return setProblem("Nothing changed yet.");

    save.mutate(changes);
  };

  return (
    <form className="card stack" style={{ gap: "var(--sp-3)" }} onSubmit={submit}>
      <div>
        <h2 className="card__title" style={{ marginBottom: 0 }}>
          Profile
        </h2>
        <p className="stat__label" style={{ margin: "var(--sp-1) 0 0" }}>
          Changing your timezone moves which date counts as today. Entries
          already logged keep the day they were filed under.
        </p>
      </div>

      <label className="stack" style={{ gap: "var(--sp-1)" }}>
        <span>Timezone</span>
        <select
          className="field"
          value={timezone}
          onChange={(e) => edit(setTimezone)(e.target.value)}
          aria-label="Timezone"
        >
          {zones.map((zone) => (
            <option key={zone} value={zone}>
              {zone.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        {/* The consequence, stated rather than implied — this is the field a
            user cannot verify by any other means on this screen. */}
        <span className="stat__label">
          today here is <span className="num">{localDate(timezone)}</span>
        </span>
      </label>

      <label className="stack" style={{ gap: "var(--sp-1)" }}>
        <span>Sex</span>
        <select
          className="field"
          value={sex}
          onChange={(e) => edit(setSex)(e.target.value as Profile["sex"])}
          aria-label="Sex"
        >
          {SEXES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <label className="manual__row">
        <span>Date of birth</span>
        <input
          type="date"
          className="field num"
          style={{ width: 168 }}
          value={birthDate}
          onChange={(e) => edit(setBirthDate)(e.target.value)}
          aria-label="Date of birth"
        />
      </label>

      <label className="manual__row">
        <span>
          Height
          <span className="muted"> cm</span>
        </span>
        <input
          className="field num manual__value"
          inputMode="numeric"
          value={height}
          onChange={(e) => edit(setHeight)(e.target.value)}
          placeholder="175"
          aria-label="Height in centimetres"
        />
      </label>

      <label className="stack" style={{ gap: "var(--sp-1)" }}>
        <span>Activity</span>
        <select
          className="field"
          value={activity}
          onChange={(e) => edit(setActivity)(e.target.value as Profile["activity_level"])}
          aria-label="Activity level"
        >
          {ACTIVITY.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <label className="stack" style={{ gap: "var(--sp-1)" }}>
        <span>Direction</span>
        <select
          className="field"
          value={goal}
          onChange={(e) => edit(setGoal)(e.target.value as Profile["goal"])}
          aria-label="Goal direction"
        >
          {GOALS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}
      {save.isError && (
        <p className="error" style={{ margin: 0 }}>
          Couldn't save your profile. {save.error?.message ?? "Try again."}
        </p>
      )}
      {save.isSuccess && (
        <p className="muted" style={{ margin: 0 }}>
          Saved.
        </p>
      )}

      <button className="button" type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save profile"}
      </button>
    </form>
  );
}
